import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Row,
    Button,
    Form,
    Spinner, Container,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getCompanyById,
  addCompany,
  updateCompany,
  addCompanySignature,
  addEstablishment,
  updateEstablishment,
  deleteEstablishment,
} from "../../../slices/companies/thunk";
import { validatePassword } from "../../../api/api";

import EstablishmentsManager from "./components/EstablishmentsManager";
import SignatureForm from "./components/SignatureForm";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { CompanyLogo, EnvironmentBadge, CompanyForm, PasswordConfirmationModal } from "./components";
import { Company, Establishment, CompanyFormValues, JWTPayload } from "./components/types";

// ===== CUSTOM HOOKS =====

// JWT Token Hook
const useJwtToken = (): string | null => {
    return useMemo(() => {
        const authUser = localStorage.getItem("authUser");
        if (!authUser) return null;

        try {
            const parsed = JSON.parse(authUser);
            if (!parsed.token) return null;
            
            const tokenParts = parsed.token.split('.');
            if (tokenParts.length !== 3) return null;
            
            const jsonPayload = atob(tokenParts[1]);
            const decoded: JWTPayload = JSON.parse(jsonPayload);
            return decoded?.companyId || null;
        } catch {
            return null;
        }
    }, []);
};

// Form Validation Hook
const useCompanyFormValidation = (company: Company | null, onSubmit: () => void) => {
    return useFormik<CompanyFormValues>({
        enableReinitialize: true,
        initialValues: {
            ruc: company?.ruc || "",
            businessName: company?.businessName || "",
            tradeName: company?.tradeName || "",
            address: company?.address || "",
            obligedAccounting: company?.obligedAccounting || false,
            rimpeType: company?.rimpeType || "REGIMEN_GENERAL",
            agentRetentionResolution: company?.agentRetentionResolution || "",
            environmentType: company?.environmentType || 1,
            logoUrl: company?.logoUrl || "",
        },
        validationSchema: Yup.object({
            ruc: Yup.string().required("RUC es requerido").length(13, "El RUC debe tener 13 dígitos"),
            businessName: Yup.string().required("Razón Social es requerida"),
            tradeName: Yup.string().required("Nombre Comercial es requerida"),
            address: Yup.string().required("Dirección es requerida"),
        }),
        onSubmit,
    });
};

// Business Logic Hook
const useCompanyBusinessLogic = (
    company: Company | null,
    isCreating: boolean,
    isEditing: boolean,
    dispatch: any,
    navigate: any,
    formValidation: any,
    setIsEditing: (value: boolean) => void
) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingSignature, setIsSavingSignature] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isConfirmingPassword, setIsConfirmingPassword] = useState(false);
    const [isPasswordValidated, setIsPasswordValidated] = useState(false);
    const [error, setError] = useState('');
    const pendingActionRef = useRef<(() => void) | null>(null);

    // Helper function para refrescar datos de la compañía
    const refreshCompanyData = useCallback(() => {
        if (company?.id) {
            dispatch(getCompanyById(company.id));
        }
    }, [dispatch, company?.id]);

    const executeWithPasswordValidation = useCallback((callback: () => void) => {
        if (isPasswordValidated) {
            callback();
        } else {
            setError(''); // Limpiar error anterior
            pendingActionRef.current = callback;
            setIsPasswordModalOpen(true);
        }
    }, [isPasswordValidated]);

    const handleUpdateCompany = useCallback(async () => {
        setIsSaving(true);
        const payload = {
            ruc: formValidation.values.ruc,
            businessName: formValidation.values.businessName,
            tradeName: formValidation.values.tradeName,
            address: formValidation.values.address,
            obligedAccounting: formValidation.values.obligedAccounting,
            rimpeType: formValidation.values.rimpeType,
            agentRetentionResolution: formValidation.values.agentRetentionResolution,
            environmentType: formValidation.values.environmentType,
            logoUrl: formValidation.values.logoUrl,
        };

        try {
            if (isCreating) {
                const action = await dispatch(addCompany(payload));
                if (action.type.endsWith('fulfilled')) {
                    navigate("/company");
                }
            } else {
                if (company?.id) {
                    await dispatch(updateCompany({ ...payload, id: company.id }));
                    setIsEditing(false);
                    refreshCompanyData();
                }
            }
        } finally {
            setIsSaving(false);
        }
    }, [dispatch, isCreating, company?.id, formValidation.values, navigate, setIsEditing, refreshCompanyData]);

    const handleConfirmPassword = useCallback(async (password: string) => {
        setIsConfirmingPassword(true);
        try {
            if (isPasswordValidated) {
                if (pendingActionRef.current) {
                    pendingActionRef.current();
                    pendingActionRef.current = null;
                }
            } else {
                const response = await validatePassword(password) as any;
                const isValid = response.valid === true;
                
                if (isValid) {
                    setIsPasswordValidated(true);
                    setIsPasswordModalOpen(false);

                    if (pendingActionRef.current) {
                        pendingActionRef.current();
                        pendingActionRef.current = null;
                    }
                } else {
                    setError(response.message);
                }
            }
        } catch (error) {
            setError('Error al validar la contraseña');
        } finally {
            setIsConfirmingPassword(false);
        }
    }, [isPasswordValidated]);

    const handleSubmitSignature = useCallback(async (values: any, resetForm: any) => {
        setIsSavingSignature(true);
        try {
            await dispatch(addCompanySignature(values));
            refreshCompanyData();
            resetForm();
        } finally {
            setIsSavingSignature(false);
        }
    }, [dispatch, refreshCompanyData]);

    const handleSaveEstablishment = useCallback(async (values: any, isEdit: boolean, callback: () => void) => {
        if (!company?.id) return;
        
        const payload = { ...values, companyId: company.id };
        const action = isEdit ? updateEstablishment(payload) : addEstablishment(payload);
        
        await dispatch(action);
        refreshCompanyData();
        callback();
    }, [dispatch, company?.id, refreshCompanyData]);

    // Versiones wrapped que usan validación de contraseña
    const handleSubmitSignatureWithValidation = useCallback(async (values: any, resetForm: any) => {
        executeWithPasswordValidation(async () => {
            await handleSubmitSignature(values, resetForm);
        });
    }, [executeWithPasswordValidation, handleSubmitSignature]);

    const handleSaveEstablishmentWithValidation = useCallback(async (values: any, isEdit: boolean, callback: () => void) => {
        executeWithPasswordValidation(async () => {
            await handleSaveEstablishment(values, isEdit, callback);
        });
    }, [executeWithPasswordValidation, handleSaveEstablishment]);

    const handleDeleteEstablishment = useCallback((id: string) => {
        dispatch(deleteEstablishment(id)).then(() => {
            refreshCompanyData();
        });
    }, [dispatch, refreshCompanyData]);

    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
        formValidation.resetForm();
    }, [setIsEditing, formValidation]);

    return {
        isSaving,
        isSavingSignature,
        isPasswordModalOpen,
        isConfirmingPassword,
        setIsPasswordModalOpen,
        isPasswordValidated,
        error,
        setError,
        executeWithPasswordValidation,
        handleConfirmPassword,
        handleUpdateCompany,
        handleSubmitSignature,
        handleSubmitSignatureWithValidation,
        handleSaveEstablishment,
        handleSaveEstablishmentWithValidation,
        handleDeleteEstablishment,
        handleCancelEdit,
    };
};

// Data Loading Hook
const useCompanyDataLoading = (
    companyIdFromLocation: string | undefined,
    userCompanyId: string | null,
    isCreatingFromLocation: boolean,
    dispatch: any,
    isLoading: boolean
) => {
    const [company, setCompany] = useState<Company | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [establishments, setEstablishments] = useState<Establishment[]>([]);
    
    const fetchAttemptedRef = useRef(false);

    // Reset fetch attempt when navigation parameters change
    useEffect(() => {
        fetchAttemptedRef.current = false;
    }, [companyIdFromLocation, userCompanyId, isCreatingFromLocation]);

    // Main data loading effect
    useEffect(() => {
        if (fetchAttemptedRef.current || isLoading) return;

        if (isCreatingFromLocation) {
            setIsCreating(true);
            setIsEditing(true);
            setCompany({
                ruc: "",
                businessName: "",
                tradeName: "",
                address: "",
                obligedAccounting: false,
                rimpeType: "REGIMEN_GENERAL",
                agentRetentionResolution: "",
                environmentType: 1,
                logoUrl: "",
            });
            fetchAttemptedRef.current = true;
        } else {
            setIsCreating(false);
            setIsEditing(false);
            
            const targetCompanyId = companyIdFromLocation || userCompanyId;
            fetchAttemptedRef.current = true;

            if (targetCompanyId) {
                dispatch(getCompanyById(targetCompanyId));
            }
        }
    }, [companyIdFromLocation, isCreatingFromLocation, dispatch, userCompanyId, isLoading]);

    // Update company state when selectedCompany changes
    const selectedCompany = useSelector((state: any) => state.Companies.selectedCompany);
    useEffect(() => {
        if (selectedCompany && !isCreating) {
            setCompany(selectedCompany);
        }
    }, [selectedCompany, isCreating]);

    // Update establishments state
    const establishmentList = useSelector((state: any) => state.Companies.establishmentList);
    useEffect(() => {
        if (establishmentList) {
            setEstablishments(establishmentList);
        } else {
            setEstablishments([]);
        }
    }, [establishmentList]);

    return {
        company,
        isEditing,
        isCreating,
        establishments,
        setCompany,
        setIsEditing,
        setIsCreating,
    };
};

// ===== MAIN COMPONENT =====
const CompanyProfile = () => {
    document.title = "Mi Compañía | Vuelo";

    // Redux and Router
    const dispatch: any = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    // Redux selectors
    const isLoading = useSelector((state: any) => state.Companies.isLoading);
    const isSavingEstablishment = useSelector((state: any) => state.Companies.isSaving); // Now from Redux
    const currentSignature = useSelector((state: any) => state.Companies.currentSignature);

    // Custom hooks
    const userCompanyId = useJwtToken();
    const companyIdFromLocation = location.state?.company?.id;
    const isCreatingFromLocation = location.state?.isCreating;

    const {
        company,
        isEditing,
        isCreating,
        establishments,
        setIsEditing,
    } = useCompanyDataLoading(
        companyIdFromLocation,
        userCompanyId,
        isCreatingFromLocation,
        dispatch,
        isLoading
    );

    // Handle form submission using ref to avoid circular dependency
    const handleFormSubmitRef = useRef(() => {});
    
    const formValidation = useCompanyFormValidation(company, () => handleFormSubmitRef.current());

    const businessLogic = useCompanyBusinessLogic(
        company,
        isCreating,
        isEditing,
        dispatch,
        navigate,
        formValidation,
        setIsEditing
    );

    // Set the ref after businessLogic is available
    useEffect(() => {
        handleFormSubmitRef.current = () => {
            businessLogic.executeWithPasswordValidation(async () => {
                await businessLogic.handleUpdateCompany();
            });
        };
    }, [businessLogic]);

    // Memoized computed values
    const isEditOrCreate = useMemo(() => isEditing || isCreating, [isEditing, isCreating]);

    // Loading states
    if (isLoading && !company && !isCreating) {
        return (
            <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (!company && !isLoading && !isCreating) {
        return (
            <div className="page-content text-center">
                <p>No se encontró información de la compañía.</p>
                <Button color="primary" onClick={() => navigate("/company")}>Volver a la lista</Button>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content" style={{ overflowX: "hidden" }}>
                <BreadCrumb title="Mi compañía" pageTitle="Inicio" />
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card className="h-100 overflow-hidden">
                                <div className="bg-primary profile-wid-bg position-relative" style={{ height: '120px' }}>
                                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-soft-dark opacity-25"></div>
                                </div>
                                <CardBody className="pt-0">
                                    <Form onSubmit={(e) => { e.preventDefault(); formValidation.handleSubmit(); }}>
                                        <div className="text-center">
                                            <CompanyLogo 
                                                company={company} 
                                                logoUrl={formValidation.values.logoUrl} 
                                            />

                                            <div className="mb-3">
                                                <EnvironmentBadge 
                                                    company={company}
                                                    isEditOrCreate={isEditOrCreate}
                                                    value={formValidation.values.environmentType}
                                                    onChange={formValidation.handleChange}
                                                />
                                            </div>
                                        </div>

                                        <CompanyForm 
                                            company={company}
                                            isEditOrCreate={isEditOrCreate}
                                            formValidation={formValidation}
                                        />

                                        <div className="d-flex justify-content-end">
                                            {isEditOrCreate ? (
                                                <div className="d-flex gap-2">
                                                    <Button color="light" onClick={businessLogic.handleCancelEdit} disabled={businessLogic.isSaving}>
                                                        Cancelar
                                                    </Button>
                                                    <Button
                                                        color="primary"
                                                        className="waves-effect waves-light"
                                                        type="submit"
                                                        disabled={businessLogic.isSaving}
                                                    >
                                                        {businessLogic.isSaving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Guardando...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line label-icon align-middle fs-16 me-2"></i>
                                                                {isCreating ? "Crear Compañía" : "Guardar Cambios"}
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button 
                                                    color="warning" 
                                                    className="waves-effect waves-light"
                                                    onClick={() => setIsEditing(true)}
                                                >
                                                    <i className="ri-pencil-line label-icon align-middle fs-16 me-2"></i> Editar Compañia
                                                </Button>
                                            )}
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {!isCreating && company && (
                        <Row className="mt-4">
                            <Col lg={6} md={12} className="mb-3">
                                <Card className="h-100">
                                    <CardHeader className="p-3 border-bottom">
                                        <h5 className="card-title mb-0">
                                            <i className="ri-community-line me-1 align-bottom"></i> Establecimientos
                                        </h5>
                                    </CardHeader>
                                    <CardBody className="p-4">
                                        {company.id && (
                                            <EstablishmentsManager
                                                companyId={company.id}
                                                establishments={establishments}
                                                onSave={businessLogic.handleSaveEstablishmentWithValidation}
                                                onDelete={businessLogic.handleDeleteEstablishment}
                                                isSaving={isSavingEstablishment}
                                                executeWithPasswordValidation={businessLogic.executeWithPasswordValidation}
                                            />
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col lg={6} md={12} className="mb-3">
                                <Card className="h-100">
                                    <CardHeader className="p-3 border-bottom">
                                        <h5 className="card-title mb-0">
                                            <i className="ri-fingerprint-line me-1 align-bottom"></i> Firma Digital
                                        </h5>
                                    </CardHeader>
                                    <CardBody className="p-4">
                                        {company.id && (
                                            <SignatureForm
                                                company={company}
                                                onSubmit={businessLogic.handleSubmitSignatureWithValidation}
                                                currentSignature={currentSignature}
                                                isSaving={businessLogic.isSavingSignature}
                                            />
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Container>

                <PasswordConfirmationModal
                    isOpen={businessLogic.isPasswordModalOpen}
                    onCancel={() => businessLogic.setIsPasswordModalOpen(false)}
                    onConfirm={businessLogic.handleConfirmPassword}
                    isConfirming={businessLogic.isConfirmingPassword}
                />
            </div>
        </React.Fragment>
    );
};

export default CompanyProfile;
